import {VectorTile} from '@mapbox/vector-tile';
import Protobuf from 'pbf';
import WorkerTile from './worker_tile';
import {getPerformanceMeasurement} from '../util/performance';
import {Evented} from '../util/evented';
import {loadVectorTile, DedupedRequest} from './load_vector_tile';
import {getExpiryDataFromHeaders} from '../util/util';

import type {
    WorkerSource,
    WorkerSourceOptions,
    WorkerSourceTileRequest,
    WorkerSourceVectorTileRequest,
    WorkerSourceVectorTileResult,
    WorkerSourceVectorTileCallback,
} from './worker_source';
import type Actor from '../util/actor';
import type StyleLayerIndex from '../style/style_layer_index';
import type Scheduler from '../util/scheduler';
import type {TaskMetadata} from '../util/scheduler';
import type {LoadVectorData, LoadVectorDataCallback} from './load_vector_tile';
import type {Cancelable} from '../types/cancelable';
import type {TileProvider} from './tile_provider';
import type {ImageId} from '../style-spec/expression/types/image_id';
import type {StyleModelMap} from '../style/style_mode';

/**
 * The {@link WorkerSource} implementation that supports {@link VectorTileSource}.
 * This class is designed to be easily reused to support custom source types
 * for data formats that can be parsed/converted into an in-memory VectorTile
 * representation.
 *
 * @private
 */
class VectorTileWorkerSource extends Evented implements WorkerSource {
    actor: Actor;
    layerIndex: StyleLayerIndex;
    availableImages: ImageId[];
    availableModels: StyleModelMap;
    loadVectorData: LoadVectorData;
    tileProvider?: TileProvider<ArrayBuffer>;
    loading: Record<number, WorkerTile>;
    loaded: Record<number, WorkerTile>;
    deduped: DedupedRequest;
    isSpriteLoaded: boolean;
    scheduler?: Scheduler | null;
    brightness?: number | null;
    maxUniformBufferBindings?: number | null;
    maxUniformBlockSizeDwords?: number | null;
    disableSymbolUBO?: boolean | null;

    /**
     * @private
     */
    constructor({actor, layerIndex, availableImages, availableModels, isSpriteLoaded, tileProvider, brightness, maxUniformBufferBindings, maxUniformBlockSizeDwords, disableSymbolUBO}: WorkerSourceOptions) {
        super();
        this.actor = actor;
        this.layerIndex = layerIndex;
        this.availableImages = availableImages;
        this.availableModels = availableModels;
        this.loadVectorData = loadVectorTile;
        this.tileProvider = tileProvider;
        this.loading = {};
        this.loaded = {};
        this.deduped = new DedupedRequest(actor.scheduler);
        this.isSpriteLoaded = isSpriteLoaded;
        this.scheduler = actor.scheduler;
        this.brightness = brightness;
        this.maxUniformBufferBindings = maxUniformBufferBindings;
        this.maxUniformBlockSizeDwords = maxUniformBlockSizeDwords;
        this.disableSymbolUBO = disableSymbolUBO;
    }

    /**
     * Loads tile data using the provider if available, otherwise falls back to loadVectorData.
     * @private
     */
    loadTileData(params: WorkerSourceVectorTileRequest, callback: LoadVectorDataCallback): Cancelable['cancel'] {
        if (!this.tileProvider) {
            return this.loadVectorData(params, callback);
        }

        const controller = new AbortController();
        const {z, x, y} = params.tileID.canonical;

        this.tileProvider.loadTile({z, x, y}, {request: params.request, signal: controller.signal})
            .then(response => {
                if (controller.signal.aborted) return;

                // Produce a 404 error so SourceCache triggers parent tile lookup for the missing tile
                if (response == null) {
                    const err: Error & {status?: number} = new Error('Tile not found');
                    err.status = 404;
                    return callback(err);
                }

                // Intentionally empty tile
                if (response.data === null) {
                    return callback(null, null);
                }

                const headers = new Map<string, string>();
                if (response.expires) headers.set('expires', response.expires);
                if (response.cacheControl) headers.set('cache-control', response.cacheControl);

                callback(null, {rawData: response.data, responseHeaders: headers});
            })
            .catch(err => {
                if (controller.signal.aborted) return;
                callback(err instanceof Error ? err : new Error(String(err)));
            });

        return () => controller.abort();
    }

    /**
     * Implements {@link WorkerSource#loadTile}. Delegates to
     * {@link VectorTileWorkerSource#loadTileData} for fetching raw tile data.
     * @private
     */
    loadTile(params: WorkerSourceVectorTileRequest, callback: WorkerSourceVectorTileCallback) {
        const uid = params.uid;

        const requestParam = params && params.request;
        const perf = requestParam && requestParam.collectResourceTiming;

        const workerTile = this.loading[uid] = new WorkerTile(params);
        workerTile.maxUniformBufferBindings = this.maxUniformBufferBindings;
        workerTile.maxUniformBlockSizeDwords = this.maxUniformBlockSizeDwords;
        workerTile.disableSymbolUBO = this.disableSymbolUBO;
        workerTile.abort = this.loadTileData(params, (err, response) => {
            const aborted = !this.loading[uid];

            delete this.loading[uid];

            workerTile.cancelRasterize();

            if (aborted || err || !response) {
                workerTile.status = 'done';
                if (!aborted) this.loaded[uid] = workerTile;
                return callback(err);
            }

            const rawTileData = response.rawData;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cacheControl: Record<string, any> = {};
            const expiryData = getExpiryDataFromHeaders(response.responseHeaders);
            if (expiryData && expiryData.expires) cacheControl.expires = expiryData.expires;
            if (expiryData && expiryData.cacheControl) cacheControl.cacheControl = expiryData.cacheControl;

            // response.vectorTile will be present in the GeoJSON worker case (which inherits from this class)
            // because we stub the vector tile interface around JSON data instead of parsing it directly
            workerTile.vectorTile = response.vectorTile || new VectorTile(new Protobuf(rawTileData));
            const parseTile = () => {
                const WorkerSourceVectorTileCallback = (err?: Error | null, result?: WorkerSourceVectorTileResult | null) => {
                    if (err || !result) return callback(err);

                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const resourceTiming: Record<string, any> = {};
                    if (perf) {
                        // Transferring a copy of rawTileData because the worker needs to retain its copy.
                        const resourceTimingData = getPerformanceMeasurement(requestParam);
                        // it's necessary to eval the result of getEntriesByName() here via parse/stringify
                        // late evaluation in the main thread causes TypeError: illegal invocation
                        if (resourceTimingData.length > 0) {
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            resourceTiming.resourceTiming = JSON.parse(JSON.stringify(resourceTimingData));
                        }
                    }
                    callback(null, Object.assign({rawTileData: rawTileData.slice(0), responseHeaders: response.responseHeaders}, result, cacheControl, resourceTiming));
                };
                workerTile.parse(workerTile.vectorTile, this.layerIndex, this.availableImages, this.availableModels, this.actor, WorkerSourceVectorTileCallback);
            };

            if (this.isSpriteLoaded) {
                parseTile();
            } else {
                // Defer tile parsing until sprite is ready. Style emits 'spriteLoaded' event, which triggers the 'isSpriteLoaded' event here.
                this.once('isSpriteLoaded', () => {
                    if (this.scheduler) {
                        const metadata: TaskMetadata = {type: 'parseTile', renderSourceType: params.renderSourceType, zoom: params.tileZoom};
                        this.scheduler.add(parseTile, metadata);
                    } else {
                        parseTile();
                    }
                });
            }

            this.loaded = this.loaded || {};
            this.loaded[uid] = workerTile;
        });
    }

    /**
     * Implements {@link WorkerSource#reloadTile}.
     * @private
     */
    reloadTile(params: WorkerSourceVectorTileRequest, callback: WorkerSourceVectorTileCallback) {
        const loaded = this.loaded,
            uid = params.uid;

        if (loaded && loaded[uid]) {
            const workerTile = loaded[uid];
            workerTile.updateParameters(params);
            const done = (err?: Error | null, data?: WorkerSourceVectorTileResult | null) => {
                const reloadCallback = workerTile.reloadCallback;
                if (reloadCallback) {
                    delete workerTile.reloadCallback;
                    workerTile.parse(workerTile.vectorTile, this.layerIndex, this.availableImages, this.availableModels, this.actor, reloadCallback);
                }
                callback(err, data);
            };

            if (workerTile.status === 'parsing') {
                workerTile.reloadCallback = done;
            } else if (workerTile.status === 'done') {
                // if there was no vector tile data on the initial load, don't try and re-parse tile
                if (workerTile.vectorTile) {
                    workerTile.parse(workerTile.vectorTile, this.layerIndex, this.availableImages, this.availableModels, this.actor, done);
                } else {
                    done();
                }
            }
        } else {
            callback(null, undefined);
        }
    }

    /**
     * Implements {@link WorkerSource#abortTile}.
     * @private
     */
    abortTile(params: WorkerSourceTileRequest, callback: WorkerSourceVectorTileCallback) {
        const uid = params.uid;
        const tile = this.loading[uid];
        if (tile) {
            if (tile.abort) tile.abort();
            delete this.loading[uid];
        }
        callback();
    }

    /**
     * Implements {@link WorkerSource#removeTile}.
     * @private
     */
    removeTile(params: WorkerSourceTileRequest, callback: WorkerSourceVectorTileCallback) {
        const loaded = this.loaded,
            uid = params.uid;
        if (loaded && loaded[uid]) {
            delete loaded[uid];
        }
        callback();
    }
}

export default VectorTileWorkerSource;
